/**
 * =============================================================================
 * Página: /afiliado/redefinir-senha
 * Versão: 1.1.0
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-04 v1.1.0 - Validação de senha forte no cliente (zod)
 *            com checklist em tempo real abaixo do campo, alinhada às regras
 *            do backend; traduz mensagens de erro cruas do servidor para PT-BR.
 *   RELEASE: v1.0.0 - Versão inicial.
 *
 * Descrição:
 *   Página de destino do link de recuperação de senha.
 *   - Valida que existe uma sessão de recuperação ativa (Supabase processa
 *     o hash do token automaticamente em onAuthStateChange).
 *   - Exibe formulário para nova senha + confirmação.
 *   - Após sucesso, faz signOut e redireciona para o login.
 *
 *   Reaproveita 100% o fluxo nativo do Supabase Auth
 *   (`supabase.auth.updateUser`) — não cria nada no backend.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  affiliateLoginPath,
  affiliateForgotPath,
} from '@/lib/affiliate-urls';
import {
  useAffiliateBrandingPublic,
  buildBrandingCssVars,
} from '@/hooks/useAffiliateBranding';
import {
  Loader2,
  AlertCircle,
  Lock,
  Eye,
  EyeOff,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
// Regras de senha forte centralizadas + checklist em tempo real
import { strongPasswordSchema, translatePasswordError } from '@/lib/passwordRules';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';

export default function AffiliateResetPassword() {
  const navigate = useNavigate();
  // tenantSlug presente quando a rota é /afiliado/:tenantSlug/redefinir-senha
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const { toast } = useToast();

  // recoveryReady: detectamos um evento PASSWORD_RECOVERY ou já há sessão
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [checking, setChecking] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Branding personalizado por tenant — herda paleta/logo configurados.
  const { data: brandingData } = useAffiliateBrandingPublic({
    tenantSlug: tenantSlug ?? null,
  });
  const brandingStyle = useMemo(
    () => buildBrandingCssVars(brandingData?.branding),
    [brandingData],
  );
  const logoUrl = brandingData?.branding?.logo_url ?? null;
  const tenantName = brandingData?.tenant_name ?? null;

  /**
   * Detecção da sessão de recuperação:
   * - Supabase Auth processa o hash (#access_token=...&type=recovery)
   *   automaticamente no carregamento da página.
   * - onAuthStateChange captura PASSWORD_RECOVERY.
   * - Como fallback, checamos se já há sessão ativa.
   */
  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (!mounted) return;
        if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
          setRecoveryReady(true);
          setChecking(false);
        }
      },
    );

    // Fallback: já existe sessão
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (session) {
        setRecoveryReady(true);
      }
      setChecking(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validação client-side com as MESMAS regras do backend (evita toast cru)
    const passwordResult = strongPasswordSchema.safeParse(password);
    if (!passwordResult.success) {
      setError(passwordResult.error.errors[0].message);
      return;
    }
    if (password.length > 72) {
      setError('A senha é muito longa (máximo 72 caracteres).');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        // Rede de segurança: traduz mensagens cruas do backend para PT-BR
        setError(translatePasswordError(updateError.message));
        return;
      }

      setSuccess(true);
      toast({
        title: 'Senha redefinida!',
        description: 'Faça login novamente com sua nova senha.',
      });

      // Encerra a sessão de recovery e manda para o login
      await supabase.auth.signOut();
      setTimeout(() => navigate(affiliateLoginPath(tenantSlug), { replace: true }), 1500);
    } catch (err) {
      logger.error('AffiliateResetPassword', 'exception', err);
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="affiliate-shell min-h-[100dvh] grid md:grid-cols-2 bg-[hsl(var(--aff-bg))]"
      style={brandingStyle}
    >
      {/* Hero — Desktop */}
      <aside className="aff-hero-surface relative hidden md:flex flex-col justify-between overflow-hidden p-10 lg:p-14">
        <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))]">
          {logoUrl ? (
            <div className="flex flex-col gap-1.5">
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-14 lg:max-h-16 max-w-[220px] object-contain object-left"
              />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-80">
                Painel do Afiliado
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                <Sparkles className="h-5 w-5" />
              </div>
              <span className="text-sm font-semibold tracking-wide opacity-90">
                {tenantName ? `${tenantName} · Painel do Afiliado` : 'Painel do Afiliado'}
              </span>
            </div>
          )}
        </div>

        <div className="relative z-10 max-w-md space-y-4 text-[hsl(var(--aff-primary-foreground))]">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Defina uma
            <br />
            nova senha
          </h1>
          <p className="text-base lg:text-[17px] leading-relaxed opacity-85">
            Use uma senha forte com no mínimo 8 caracteres. Combine letras,
            números e símbolos para mais segurança.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-[hsl(var(--aff-primary-foreground))] opacity-75">
          <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
          <span className="text-xs font-medium tracking-wide uppercase">
            Programa de Indicação
          </span>
        </div>
      </aside>

      {/* Mini-hero — Mobile */}
      <div className="aff-hero-surface md:hidden relative h-[28dvh] min-h-[180px] flex items-end overflow-hidden px-6 pb-8">
        <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))] space-y-2">
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-9 max-w-[140px] object-contain object-left"
              />
            ) : (
              <>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <span className="text-[12px] font-semibold tracking-wide opacity-90">
                  Redefinir senha
                </span>
              </>
            )}
          </div>
          <h1 className="text-[24px] font-bold tracking-tight leading-tight">
            Crie uma nova senha
          </h1>
        </div>
      </div>

      {/* Coluna do formulário */}
      <main className="flex justify-center px-4 pb-8 md:p-10 lg:p-14 -mt-10 md:mt-0 relative z-10 md:items-center md:justify-center">
        <div className="w-full max-w-md">
          <div
            className="aff-card rounded-3xl p-6 md:p-8 space-y-6 aff-animate-in"
            style={{ boxShadow: 'var(--aff-shadow-hero)' }}
          >
            {checking ? (
              <div className="py-8 flex flex-col items-center gap-3 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--aff-primary))]" />
                <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                  Validando link de recuperação...
                </p>
              </div>
            ) : !recoveryReady ? (
              // Link inválido / expirado
              <div className="space-y-5 text-center py-2">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[hsl(var(--aff-danger)/0.12)] text-[hsl(var(--aff-danger))]">
                  <AlertCircle className="h-8 w-8" />
                </div>
                <div className="space-y-1.5">
                  <h2 className="text-[22px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                    Link inválido ou expirado
                  </h2>
                  <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                    Este link de recuperação não é mais válido. Solicite um
                    novo link para continuar.
                  </p>
                </div>
                <Button
                  onClick={() => navigate(affiliateForgotPath(tenantSlug))}
                  className="rounded-xl text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 border-0 font-semibold shadow-[var(--aff-shadow-soft)]"
                  style={{ background: 'var(--aff-gradient-hero)' }}
                >
                  Solicitar novo link
                </Button>
              </div>
            ) : success ? (
              // Sucesso
              <div className="space-y-5 text-center py-2">
                <div
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-[hsl(var(--aff-primary-foreground))]"
                  style={{ background: 'var(--aff-gradient-hero)' }}
                >
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <div className="space-y-1.5">
                  <h2 className="text-[22px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                    Senha redefinida!
                  </h2>
                  <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                    Redirecionando para a tela de login...
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <h2 className="text-[22px] md:text-[24px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                    Criar nova senha
                  </h2>
                  <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                    Defina sua nova senha de acesso ao painel.
                  </p>
                </div>

                {error && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-2xl bg-[hsl(var(--aff-danger)/0.10)] px-3.5 py-2.5 text-[13px] text-[hsl(var(--aff-danger))]"
                  >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span className="leading-snug">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Nova senha */}
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="password"
                      className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                    >
                      Nova senha
                    </Label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Mínimo 8 caracteres"
                        required
                        autoComplete="new-password"
                        disabled={submitting}
                        minLength={8}
                        maxLength={72}
                        className="h-11 pl-10 pr-11 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-[hsl(var(--aff-muted-foreground))] hover:text-[hsl(var(--aff-foreground))] hover:bg-[hsl(var(--aff-surface-2))] transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {/* Checklist de requisitos em tempo real (evita erro do backend) */}
                    {password.length > 0 && <PasswordRequirements password={password} />}
                  </div>

                  {/* Confirmar senha */}
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="confirm"
                      className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                    >
                      Confirmar nova senha
                    </Label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                      <Input
                        id="confirm"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Repita a nova senha"
                        required
                        autoComplete="new-password"
                        disabled={submitting}
                        minLength={8}
                        maxLength={72}
                        className="h-11 pl-10 pr-3 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-xl text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 border-0 font-semibold text-[15px] shadow-[var(--aff-shadow-soft)] transition-opacity"
                    style={{ background: 'var(--aff-gradient-hero)' }}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      'Salvar nova senha'
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
